import type { CharacterInput, Character, Player, PlayerInput } from '@moussenaires/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { rosterApi } from '../api/roster.js';
import { ApiClientError } from '../api/client.js';
import { CharacterDetailModal } from '../components/CharacterDetailModal.js';
import { CharacterFormModal } from '../components/CharacterFormModal.js';
import { CharacterQuickAddModal } from '../components/CharacterQuickAddModal.js';
import { PlayerFormModal } from '../components/PlayerFormModal.js';
import { RosterHeroWall } from '../components/RosterHeroWall.js';
import { RosterSortToggle, type RosterSortMode } from '../components/RosterSortToggle.js';
import { SpeedDial } from '../components/SpeedDial.js';
import { useAuth } from '../context/AuthContext.js';
import './RosterPage.css';

export function RosterPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';

  const [searchParams, setSearchParams] = useSearchParams();
  const sortMode: RosterSortMode =
    searchParams.get('sort') === 'mythicPlus' ? 'mythicPlus' : 'raid';
  const setSortMode = (mode: RosterSortMode) => {
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.set('sort', mode);
        return next;
      },
      { replace: true },
    );
  };
  const [editingPlayer, setEditingPlayer] = useState<Player | 'new' | null>(null);
  const [quickAddForPlayerId, setQuickAddForPlayerId] = useState<number | null>(null);
  const [addCharacterOpen, setAddCharacterOpen] = useState(false);
  const [isAddingCharacter, setIsAddingCharacter] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState<Character | null>(null);
  const [selectedCharacterId, setSelectedCharacterId] = useState<number | null>(null);

  const playersQuery = useQuery({ queryKey: ['roster', 'players'], queryFn: rosterApi.players });

  const createPlayer = useMutation({
    mutationFn: (input: PlayerInput) => rosterApi.createPlayer(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['roster'] });
      setEditingPlayer(null);
    },
  });
  const updatePlayer = useMutation({
    mutationFn: ({ id, input }: { id: number; input: PlayerInput }) =>
      rosterApi.updatePlayer(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['roster'] });
      setEditingPlayer(null);
    },
  });
  const deletePlayer = useMutation({
    mutationFn: (id: number) => rosterApi.deletePlayer(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['roster'] });
      setEditingPlayer(null);
    },
  });
  const quickAddCharacter = useMutation({
    mutationFn: ({
      playerId,
      input,
    }: {
      playerId: number;
      input: { name: string; realm: string };
    }) => rosterApi.quickAddCharacter(playerId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['roster'] });
      setQuickAddForPlayerId(null);
    },
  });
  const updateCharacter = useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<CharacterInput> }) =>
      rosterApi.updateCharacter(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['roster'] });
      setEditingCharacter(null);
    },
  });
  /**
   * The speed dial's "add character" flow isn't scoped to one player the
   * way a ghost card's quick-add already is — it can also create the player
   * on the spot, so this chains the two mutations instead of firing one.
   */
  async function submitAddCharacter(input: {
    name: string;
    realm: string;
    playerId?: number;
    newPlayerName?: string;
  }) {
    setIsAddingCharacter(true);
    try {
      const playerId = input.newPlayerName
        ? (await createPlayer.mutateAsync({ name: input.newPlayerName })).id
        : input.playerId;
      if (playerId == null) return;
      await quickAddCharacter.mutateAsync({
        playerId,
        input: { name: input.name, realm: input.realm },
      });
      setAddCharacterOpen(false);
    } finally {
      setIsAddingCharacter(false);
    }
  }

  if (playersQuery.error instanceof ApiClientError && playersQuery.error.status === 401) {
    return (
      <div className="center-state">
        <p>{t('roster.private')}</p>
      </div>
    );
  }

  if (playersQuery.isLoading) {
    return (
      <div className="center-state">
        <div className="spinner" />
      </div>
    );
  }

  const players = playersQuery.data ?? [];

  return (
    <div className="full-bleed roster-hero-page">
      <RosterSortToggle mode={sortMode} onChange={setSortMode} />

      <RosterHeroWall
        players={players}
        isAdmin={isAdmin}
        sortMode={sortMode}
        onSelectCharacter={setSelectedCharacterId}
        onAddCharacterFor={setQuickAddForPlayerId}
        onEditPlayer={(playerId) => {
          const player = players.find((item) => item.id === playerId);
          if (player) setEditingPlayer(player);
        }}
      />

      {isAdmin && <SpeedDial onAddCharacter={() => setAddCharacterOpen(true)} />}

      {editingPlayer && (
        <PlayerFormModal
          player={editingPlayer === 'new' ? undefined : editingPlayer}
          isSaving={createPlayer.isPending || updatePlayer.isPending}
          onClose={() => setEditingPlayer(null)}
          onSubmit={(input) =>
            editingPlayer === 'new'
              ? createPlayer.mutate(input)
              : updatePlayer.mutate({ id: editingPlayer.id, input })
          }
          onDelete={
            editingPlayer === 'new' ? undefined : () => deletePlayer.mutate(editingPlayer.id)
          }
        />
      )}

      {quickAddForPlayerId !== null && (
        <CharacterQuickAddModal
          isSaving={quickAddCharacter.isPending}
          onClose={() => setQuickAddForPlayerId(null)}
          onSubmit={(input) => quickAddCharacter.mutate({ playerId: quickAddForPlayerId, input })}
        />
      )}

      {addCharacterOpen && (
        <CharacterQuickAddModal
          players={players.map((player) => ({ id: player.id, name: player.name }))}
          isSaving={isAddingCharacter}
          onClose={() => setAddCharacterOpen(false)}
          onSubmit={(input) => void submitAddCharacter(input)}
        />
      )}

      {editingCharacter && (
        <CharacterFormModal
          character={editingCharacter}
          isSaving={updateCharacter.isPending}
          onClose={() => setEditingCharacter(null)}
          onSubmit={(input) => updateCharacter.mutate({ id: editingCharacter.id, input })}
        />
      )}

      {selectedCharacterId && (
        <CharacterDetailModal
          characterId={selectedCharacterId}
          isAdmin={isAdmin}
          onClose={() => setSelectedCharacterId(null)}
          onEdit={() => {
            const character = players
              .flatMap((player) => player.characters)
              .find((item) => item.id === selectedCharacterId);
            if (character) {
              setEditingCharacter(character);
              setSelectedCharacterId(null);
            }
          }}
          onDeleted={() => setSelectedCharacterId(null)}
        />
      )}
    </div>
  );
}
